/**
 * Tokenizing of @handle mentions in plain-text rec league bios.
 *
 * Pure and dependency-free on purpose: it runs client-side (page_render
 * linkification), server-side (bio mention resolution), and is mirrored on
 * mobile. Keep in sync with mobile/lib/util/bioMentionText.ts.
 */

/** A bio @mention resolved server-side to a real, viewer-visible rec league. */
export interface BioMention {
  subdomain: string;
  rec_league_id: number;
}

export interface BioMentionToken {
  /** The candidate handle, without the leading "@". */
  handle: string;
  /** Index of the "@" in the source text. */
  start: number;
  /** Index just past the last handle character. */
  end: number;
}

// Matches the subdomain charset and length rules enforced at league creation
// (RecLeague.validateSubdomain: /^[a-z0-9_]+$/, >= 5 chars; handles are capped
// at 40 by getSubdomainFromName). Tokens are only CANDIDATES — whether one
// actually names a league is decided by a DB lookup, never by the regex.
const HANDLE_TOKEN_REGEX = /@([a-z0-9_]{5,40})/g;

// An "@" glued to a preceding letter/digit (any script) or email-local-part
// punctuation is an email or mid-word "@" ("james@latchet.com",
// "josé@latchet.com", "name-@latchet.com" — "." / "+" / "-" can validly end
// an email local part). Anchored on the prefix so surrogate pairs are
// evaluated as whole code points. `(?![\s\S])` is the TRUE end of input — a
// bare `$` also matches before a trailing newline, which would wrongly block
// a mention at the start of a new paragraph ("…josé\n@handle").
const BLOCKED_BEFORE_REGEX = /[\p{L}\p{N}_@.+-](?![\s\S])/u;
// A trailing letter/digit (any script) means the token continues with
// characters a handle can't contain ("@CookingClub" is not a mention of
// "cooking", and "@abcdeé" is a longer word, not the handle "abcde").
const BLOCKED_AFTER_REGEX = /^[\p{L}\p{N}_]/u;

/**
 * Find plausible @handle tokens in a plain-text bio. Word-boundary rules keep
 * non-mentions (emails, mid-word "@", handle-prefixes of longer words) as
 * plain text — see the boundary regexes above.
 */
export function findBioMentionTokens(text: string): BioMentionToken[] {
  const tokens: BioMentionToken[] = [];
  for (const match of text.matchAll(HANDLE_TOKEN_REGEX)) {
    const start = match.index ?? 0;
    const end = start + match[0].length;
    if (BLOCKED_BEFORE_REGEX.test(text.slice(0, start))) continue;
    if (BLOCKED_AFTER_REGEX.test(text.slice(end))) continue;
    tokens.push({ handle: match[1], start, end });
  }
  return tokens;
}

/** Unique candidate handles in a bio, for resolution against real leagues. */
export function extractBioMentionHandles(text: string): string[] {
  return [...new Set(findBioMentionTokens(text).map((token) => token.handle))];
}
